import { db } from '$db/local/db';
import type { MemoryWeights, UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, notDeleted } from './common';
import { getNode } from './nodes.repo';

export interface UpsertMemoryWeightsInput {
  nodeId: UUID;
  retentionTarget: number;
  maximumIntervalDays?: number | null;
  weights?: number[];
}

export async function getMemoryWeightsForNode(
  nodeId: UUID,
  userId = getActiveUserId()
): Promise<MemoryWeights | undefined> {
  const rows = await db.memoryWeights.where('userId').equals(userId).toArray();
  return rows.find((r) => notDeleted(r) && (r.nodeId ?? null) === nodeId);
}

export async function getGlobalMemoryWeights(
  userId = getActiveUserId()
): Promise<MemoryWeights | undefined> {
  const rows = await db.memoryWeights.where('userId').equals(userId).toArray();
  return rows.find((r) => notDeleted(r) && r.nodeId === null);
}

export async function upsertMemoryWeights(
  input: UpsertMemoryWeightsInput
): Promise<MemoryWeights> {
  const existing = await getMemoryWeightsForNode(input.nodeId);
  const now = nowIso();

  if (existing) {
    const patch: Partial<MemoryWeights> = {
      retentionTarget: input.retentionTarget,
      updatedAt: now
    };
    if (input.weights !== undefined) patch.weights = input.weights;
    if (input.maximumIntervalDays !== undefined) {
      patch.maximumIntervalDays = input.maximumIntervalDays;
    }
    await db.memoryWeights.update(existing.id, patch);
    return (await db.memoryWeights.get(existing.id))!;
  }

  const record: MemoryWeights = {
    id: newId(),
    userId: getActiveUserId(),
    nodeId: input.nodeId,
    weights: input.weights ?? [],
    retentionTarget: input.retentionTarget,
    maximumIntervalDays: input.maximumIntervalDays ?? null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.memoryWeights.add(record);
  return record;
}

export async function upsertGlobalMemoryWeights(
  input: Omit<UpsertMemoryWeightsInput, 'nodeId'>
): Promise<MemoryWeights> {
  const existing = await getGlobalMemoryWeights();
  const now = nowIso();

  if (existing) {
    const patch: Partial<MemoryWeights> = {
      retentionTarget: input.retentionTarget,
      updatedAt: now
    };
    if (input.maximumIntervalDays !== undefined) {
      patch.maximumIntervalDays = input.maximumIntervalDays;
    }
    if (input.weights !== undefined) patch.weights = input.weights;
    await db.memoryWeights.update(existing.id, patch);
    return (await db.memoryWeights.get(existing.id))!;
  }

  const record: MemoryWeights = {
    id: newId(),
    userId: getActiveUserId(),
    nodeId: null as any, // Representa la configuración global
    weights: input.weights ?? [],
    retentionTarget: input.retentionTarget,
    maximumIntervalDays: input.maximumIntervalDays ?? null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.memoryWeights.add(record);
  return record;
}

export async function listMemoryWeights(
  userId = getActiveUserId()
): Promise<MemoryWeights[]> {
  const rows = await db.memoryWeights.where('userId').equals(userId).toArray();
  return rows.filter(notDeleted);
}

export async function deleteMemoryWeightsForNode(
  nodeId: UUID,
  userId = getActiveUserId()
): Promise<void> {
  const existing = await getMemoryWeightsForNode(nodeId, userId);
  if (!existing) return;
  await db.memoryWeights.update(existing.id, {
    deletedAt: nowIso(),
    updatedAt: nowIso()
  });
}

/* ============================================================
   Cadena FSRS dinámica.
   Busca en el nodo actual. Si no tiene, sube a su padre,
   abuelo, bisabuelo... hasta el nodo raíz, y luego al global.
   ============================================================ */

export interface ResolvedFsrs {
  retentionTarget: number;
  maximumIntervalDays: number;
  retentionFrom: string;
  intervalFrom: string;
}

export async function resolveFsrsForNode(
  nodeId: UUID,
  fallback: { retentionTarget: number; maximumIntervalDays: number },
  userId = getActiveUserId()
): Promise<ResolvedFsrs> {
  const rows = await listMemoryWeights(userId);
  
  let currentNodeId: string | null = nodeId;
  let retentionTarget: number | null = null;
  let retentionFrom: string = 'default';
  let intervalDays: number | null = null;
  let intervalFrom: string = 'default';

  // 1. Recorremos el árbol hacia arriba
  while (currentNodeId) {
    const row = rows.find((r) => r.nodeId === currentNodeId);
    if (row) {
      if (retentionTarget === null) {
        retentionTarget = row.retentionTarget;
        retentionFrom = currentNodeId;
      }
      if (intervalDays === null && row.maximumIntervalDays != null) {
        intervalDays = row.maximumIntervalDays;
        intervalFrom = currentNodeId;
      }
      if (retentionTarget !== null && intervalDays !== null) break;
    }
    
    const node = await getNode(currentNodeId);
    currentNodeId = node?.parentId || null;
  }

  // 2. Si aún falta algo, vamos a la configuración global (nodeId = null)
  if (retentionTarget === null || intervalDays === null) {
    const globalRow = rows.find((r) => r.nodeId === null);
    if (globalRow) {
      if (retentionTarget === null) {
        retentionTarget = globalRow.retentionTarget;
        retentionFrom = 'global';
      }
      if (intervalDays === null && globalRow.maximumIntervalDays != null) {
        intervalDays = globalRow.maximumIntervalDays;
        intervalFrom = 'global';
      }
    }
  }

  return {
    retentionTarget: retentionTarget ?? fallback.retentionTarget,
    maximumIntervalDays: intervalDays ?? fallback.maximumIntervalDays,
    retentionFrom,
    intervalFrom
  };
}